import {
  CalendarDays,
  Crown,
  HeartHandshake,
  ShieldCheck,
  Users,
} from "lucide-react";

import type { HouseholdIntelligenceResponse } from "@/api/householdIntelligence";
import type { HouseholdDetail } from "@/types/household";
import { Card } from "@/components/ui/card";

type Props = {
  household: HouseholdDetail;
  intelligence: HouseholdIntelligenceResponse;
};

export function HouseholdStatisticsCard({
  household,
  intelligence,
}: Props) {
  const headCount = household.members.filter(
    (member) => member.is_head_of_household
  ).length;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-blue-500/15 p-3 text-blue-400">
          <Users size={21} />
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white">
            Household Statistics
          </h2>

          <p className="text-sm text-slate-500">
            Key household profile and engagement metrics
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Statistic
          icon={<Users size={18} />}
          label="Members"
          value={household.members.length}
          className="text-indigo-400"
        />

        <Statistic
          icon={<Crown size={18} />}
          label="Household Heads"
          value={headCount}
          className="text-amber-400"
        />

        <Statistic
          icon={<ShieldCheck size={18} />}
          label="Health Score"
          value={intelligence.health_score}
          className={getHealthTextClass(intelligence.health_score)}
        />

        <Statistic
          icon={<CalendarDays size={18} />}
          label="Attendance"
          value={`${intelligence.attendance_rate}%`}
          className="text-emerald-400"
        />

        <Statistic
          icon={<HeartHandshake size={18} />}
          label="Open Care"
          value={intelligence.open_care_items}
          className={
            intelligence.open_care_items > 0
              ? "text-rose-400"
              : "text-emerald-400"
          }
        />
      </div>
    </Card>
  );
}

function Statistic({
  icon,
  label,
  value,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  className: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
      <div className={`flex items-center gap-2 ${className}`}>
        {icon}

        <p className="text-xs font-semibold uppercase tracking-wider">
          {label}
        </p>
      </div>

      <p className="mt-4 text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function getHealthTextClass(score: number) {
  if (score >= 80) {
    return "text-emerald-400";
  }

  if (score >= 60) {
    return "text-amber-400";
  }

  if (score >= 40) {
    return "text-orange-400";
  }

  return "text-rose-400";
}